import React, { useEffect } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import {
  AttachMoney as AttachMoneyIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { sortBy } from "underscore";

import {
  fetchOrdersForEvent,
  fetchSessionsForEvent,
  fetchTicketsForEvent,
  fetchPromoCodesForEvent,
  fetchAffiliatesForEvent,
  statusForEvent,
} from "../../../redux/features/adminEventsSlice";
import AudienceMapOSM from "./AudienceMapOSM";

const priceText = (usdCents) => {
  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  return USDollar.format((usdCents || 0) / 100);
};

const MetricCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "primary",
  progressValue,
  progressMax,
}) => {
  const progressPercentage =
    progressValue && progressMax
      ? Math.round((progressValue / progressMax) * 100)
      : null;
  const isSoldOut = progressPercentage !== null && progressPercentage >= 100;

  return (
    <Card elevation={2} sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              data-testid={`metric-card-title-${title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              fontWeight="bold"
              data-testid={`metric-card-value-${title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`metric-card-subtitle-${title
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {subtitle}
              </Typography>
            )}
            {progressPercentage !== null && (
              <Box sx={{ position: "relative", mt: 1.5 }}>
                <Box
                  sx={{
                    width: "100%",
                    height: 8,
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    overflow: "hidden",
                    position: "relative",
                  }}
                  data-testid={`metric-card-progress-${title
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  <Box
                    sx={{
                      width: `${progressPercentage}%`,
                      height: "100%",
                      backgroundColor: isSoldOut
                        ? "error.main"
                        : progressPercentage > 75
                        ? "warning.main"
                        : progressPercentage > 50
                        ? "info.main"
                        : "success.main",
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          {Icon && (
            <Box
              sx={{
                backgroundColor: `${color}.light`,
                borderRadius: "50%",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 2,
              }}
              data-testid={`metric-card-icon-${title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <Icon sx={{ fontSize: 32, color: `${color}.main` }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const TicketTypeBreakdown = ({ ticketTypes, eventId }) => {
  const navigate = useNavigate();

  if (!ticketTypes || ticketTypes.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Ticket Types
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No ticket types configured
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom data-testid="ticket-types-title">
        Ticket Types Breakdown
      </Typography>
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        {ticketTypes.map((ticketType) => {
          const { name, usdCents, stats, requiresPromoCode } = ticketType;
          const sold = stats?.sold || 0;
          const capacity = stats?.capacity;
          const remaining = capacity ? capacity - sold : null;
          const soldOut = capacity && sold >= capacity;
          const percentage = capacity
            ? Math.round((sold / capacity) * 100)
            : null;

          return (
            <Box
              key={ticketType.key}
              data-testid={`ticket-type-${ticketType.key}`}
            >
              <Box
                component={Link}
                to={`/events/${eventId}/ticketTypes/${ticketType.key}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                data-testid={`ticket-type-details-link-${ticketType.key}`}
              >
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {requiresPromoCode && (
                      <Tooltip title="Hidden (reveal with promo code)">
                        <VisibilityOffIcon
                          sx={{ fontSize: 18, color: "error.main" }}
                          data-testid={`ticket-type-hidden-indicator-${ticketType.key}`}
                        />
                      </Tooltip>
                    )}
                    <Typography variant="body1" fontWeight="medium">
                      {name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {priceText(usdCents)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  {capacity ? (
                    <>
                      <Button
                        variant="text"
                        size="small"
                        sx={{
                          padding: 0,
                          minWidth: 0,
                          textTransform: "none",
                          justifyContent: "flex-end",
                          color: "primary.main",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        data-testid={`ticket-type-attendees-link-${ticketType.key}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(
                            `/events/${eventId}/tickets?ticketType=${encodeURIComponent(ticketType.key)}`,
                          );
                        }}
                      >
                        {sold} / {capacity}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        {remaining !== null
                          ? `${remaining} remaining`
                          : "Unlimited"}
                      </Typography>
                    </>
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        padding: 0,
                        minWidth: 0,
                        textTransform: "none",
                        justifyContent: "flex-end",
                        color: "primary.main",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      data-testid={`ticket-type-attendees-link-${ticketType.key}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(
                          `/events/${eventId}/tickets?ticketType=${encodeURIComponent(ticketType.key)}`,
                        );
                      }}
                    >
                      {sold} sold
                    </Button>
                  )}
                </Box>
              </Box>
              {capacity && (
                <Box sx={{ position: "relative", mt: 1 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 12,
                      backgroundColor: "grey.200",
                      borderRadius: 1,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${percentage}%`,
                        height: "100%",
                        backgroundColor: soldOut
                          ? "error.main"
                          : percentage > 75
                          ? "warning.main"
                          : percentage > 50
                          ? "info.main"
                          : "success.main",
                        transition: "width 0.3s ease",
                      }}
                      data-testid={`ticket-type-progress-${ticketType.key}`}
                    />
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                        color:
                          percentage > 50 ? "common.white" : "text.primary",
                        textShadow:
                          percentage > 50
                            ? "0 1px 2px rgba(0,0,0,0.3)"
                            : "none",
                      }}
                    >
                      {percentage}%
                    </Typography>
                  </Box>
                </Box>
              )}
              {soldOut && (
                <Chip
                  label="Sold Out"
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                  data-testid={`ticket-type-sold-out-${ticketType.key}`}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const RecentOrders = ({ orders, eventId }) => {
  if (!orders || orders.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Orders
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No orders yet
        </Typography>
      </Paper>
    );
  }

  const sortedOrders = (
    sortBy(orders, (order) => {
      return order.createdAt?.toDate?.()?.getTime() || 0;
    }) || []
  ).reverse();

  const recentOrders = sortedOrders.slice(0, 5);
  const totalOrders = orders.length;
  const hasMoreOrders = totalOrders > 5;

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" gutterBottom data-testid="recent-orders-title">
          Recent Orders
        </Typography>
        {hasMoreOrders && (
          <Typography variant="caption" color="text.secondary">
            {totalOrders} total
          </Typography>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          flex: 1,
        }}
        data-testid="recent-orders-list"
      >
        {recentOrders.map((order) => {
          const orderDate = order.createdAt?.toDate
            ? format(order.createdAt.toDate(), "MMM d, h:mm a")
            : "Unknown date";
          const totalTickets = (order.items || []).reduce(
            (sum, item) => sum + (item.quantity || 0),
            0,
          );

          return (
            <Box key={order.id} data-testid={`recent-order-${order.id}`}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Order #{order.id.slice(-8)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {orderDate} • {totalTickets}{" "}
                    {totalTickets === 1 ? "ticket" : "tickets"}
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {priceText(order.totalUsdCents)}
                </Typography>
              </Box>
              {order !== recentOrders[recentOrders.length - 1] && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          );
        })}
      </Box>
      {hasMoreOrders && (
        <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
          <Button
            component={Link}
            to={`/events/${eventId}/orders`}
            variant="outlined"
            fullWidth
            data-testid="view-all-orders-button"
          >
            View All Orders ({totalOrders})
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const PromoCodeUsage = ({ promoCodes, tickets, eventId }) => {
  const navigate = useNavigate();
  if (!promoCodes || promoCodes.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Promo Code Usage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No promo codes configured
        </Typography>
      </Paper>
    );
  }

  // Calculate actual usage from tickets
  const promoCodeUsage = promoCodes.map((promoCode) => {
    const ticketsWithPromo = (tickets || []).filter(
      (ticket) =>
        ticket.appliedPromoCode && ticket.promoCode === promoCode.name,
    ).length;

    return {
      ...promoCode,
      actualUsage: ticketsWithPromo,
    };
  });

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Typography
        variant="h6"
        gutterBottom
        data-testid="promo-code-usage-title"
      >
        Promo Code Usage
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mt={2}
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          flex: 1,
          pb: 1,
        }}
        data-testid="promo-code-usage-list"
      >
        {promoCodeUsage.map((promoCode) => {
          const {
            id,
            name,
            capacity,
            timesUsed,
            actualUsage,
            discountType,
            discountAmount,
          } = promoCode;

          let discountText;
          switch (discountType) {
            case "percentage":
              discountText = `${(discountAmount / 100).toFixed(2)}%`;
              break;
            case "usdCents":
              discountText = priceText(discountAmount);
              break;
            default:
              discountText = "N/A";
          }

          const usage = actualUsage || timesUsed || 0;
          const remaining = capacity ? capacity - usage : null;
          const soldOut = capacity && usage >= capacity;
          const percentage = capacity
            ? Math.round((usage / capacity) * 100)
            : null;

          return (
            <Box key={id} data-testid={`promo-code-${id}`}>
              <Box
                component={Link}
                to={`/events/${eventId}/promoCodes/${id}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                data-testid={`promo-code-details-link-${id}`}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discount: {discountText}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      padding: 0,
                      minWidth: 0,
                      textTransform: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    data-testid={`promo-code-orders-link-${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(
                        `/events/${eventId}/orders?promoCode=${encodeURIComponent(
                          name || "",
                        )}`,
                      );
                    }}
                  >
                    {usage} used
                  </Button>
                  {capacity && (
                    <Typography variant="body2" color="text.secondary">
                      {remaining !== null
                        ? `${remaining} remaining`
                        : "Unlimited"}
                    </Typography>
                  )}
                </Box>
              </Box>
              {capacity && (
                <Box
                  sx={{
                    width: "100%",
                    height: 8,
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: "100%",
                      backgroundColor: soldOut
                        ? "error.main"
                        : percentage > 75
                        ? "warning.main"
                        : "success.main",
                    }}
                    data-testid={`promo-code-progress-${id}`}
                  />
                </Box>
              )}
              {soldOut && (
                <Chip
                  label="Sold Out"
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                  data-testid={`promo-code-sold-out-${id}`}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const SessionsOverview = ({ sessions, event }) => {
  if (!sessions || sessions.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sessions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No sessions configured
        </Typography>
      </Paper>
    );
  }

  const { timezone } = event || {};

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Typography
        variant="h6"
        gutterBottom
        data-testid="sessions-overview-title"
      >
        Sessions
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mt={2}
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          flex: 1,
          pb: 1,
        }}
        data-testid="sessions-overview-list"
      >
        {sessions.map((session) => {
          const { id, name, startsAt, totalTickets, totalTicketsCheckedIn } =
            session;

          const checkInRate =
            totalTickets > 0
              ? Math.round((totalTicketsCheckedIn / totalTickets) * 100)
              : 0;

          let dateTimeText = "";
          if (startsAt && timezone) {
            try {
              const dateText = formatInTimeZone(
                startsAt.toDate(),
                timezone,
                "MMM d",
              );
              const timeText = formatInTimeZone(
                startsAt.toDate(),
                timezone,
                "h:mm a",
              );
              dateTimeText = `${dateText} at ${timeText}`;
            } catch (e) {
              // Fallback if date formatting fails
              dateTimeText = "";
            }
          }

          return (
            <Box key={id} data-testid={`session-${id}`}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {name || "Unnamed Session"}
                  </Typography>
                  {dateTimeText && (
                    <Typography variant="body2" color="text.secondary">
                      {dateTimeText}
                    </Typography>
                  )}
                </Box>
                <Box textAlign="right">
                  <Typography variant="body1" fontWeight="medium">
                    {totalTicketsCheckedIn} / {totalTickets}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {checkInRate}% checked in
                  </Typography>
                </Box>
              </Box>
              {totalTickets > 0 && (
                <Box sx={{ position: "relative", mt: 1 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      backgroundColor: "grey.200",
                      borderRadius: 1,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${checkInRate}%`,
                        height: "100%",
                        backgroundColor:
                          checkInRate > 75
                            ? "success.main"
                            : checkInRate > 50
                            ? "info.main"
                            : checkInRate > 25
                            ? "warning.main"
                            : "error.main",
                        transition: "width 0.3s ease",
                      }}
                      data-testid={`session-progress-${id}`}
                    />
                  </Box>
                </Box>
              )}
              {session !== sessions[sessions.length - 1] && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const AffiliatesPerformance = ({ affiliates, tickets, orders, eventId }) => {
  const navigate = useNavigate();
  if (!affiliates || affiliates.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Affiliates Performance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No affiliates configured
        </Typography>
      </Paper>
    );
  }

  // Use backend-maintained totals (refund-adjusted); fallback to ticket count from local data
  const affiliatePerformance = affiliates.map((affiliate) => {
    const ticketsWithAffiliate = (tickets || []).filter(
      (ticket) => ticket.affiliate === affiliate.name,
    ).length;

    return {
      ...affiliate,
      actualTicketsSold: ticketsWithAffiliate,
      actualRevenue: affiliate.totalUsdCents ?? 0,
    };
  });

  // Sort by revenue descending
  const sortedAffiliates = sortBy(
    affiliatePerformance,
    (aff) => -aff.actualRevenue,
  );

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Typography
        variant="h6"
        gutterBottom
        data-testid="affiliates-performance-title"
      >
        Affiliates Performance
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mt={2}
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          flex: 1,
          pb: 1,
        }}
        data-testid="affiliates-performance-list"
      >
        {sortedAffiliates.map((affiliate) => {
          const { id, name, actualTicketsSold, actualRevenue } = affiliate;

          const ticketsSold = actualTicketsSold || affiliate.ticketsSold || 0;

          return (
            <Box key={id} data-testid={`affiliate-${id}`}>
              <Box
                component={Link}
                to={`/events/${eventId}/affiliates/${id}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                data-testid={`affiliate-details-link-${id}`}
              >
                <Box display="flex" flexDirection="column">
                  <Typography variant="body1" fontWeight="medium">
                    {name}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      padding: 0,
                      minWidth: 0,
                      textTransform: "none",
                      justifyContent: "flex-start",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    data-testid={`affiliate-orders-link-${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(
                        `/events/${eventId}/orders?affiliate=${encodeURIComponent(
                          name || "",
                        )}`,
                      );
                    }}
                  >
                    {ticketsSold}{" "}
                    {ticketsSold === 1 ? "ticket" : "tickets"} sold
                  </Button>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {priceText(actualRevenue)}
                </Typography>
              </Box>
              {affiliate !== sortedAffiliates[sortedAffiliates.length - 1] && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const EventDashboard = () => {
  const { event } = useOutletContext();
  const dispatch = useDispatch();

  const {
    id: eventId,
    organizationId,
    orders,
    tickets,
    sessions,
    stats,
    ticketTypes,
    promoCodes,
    affiliates,    
    endsAt,
    publishedAt,
    squareLocationError,
  } = event || {};

  useEffect(() => {
    if (eventId) {
      dispatch(fetchOrdersForEvent({ eventId, organizationId }));
      dispatch(fetchTicketsForEvent({ eventId }));
      dispatch(fetchSessionsForEvent({ eventId }));
      dispatch(fetchPromoCodesForEvent({ eventId }));
      dispatch(fetchAffiliatesForEvent({ eventId }));
    }
  }, [dispatch, eventId, organizationId]);

  if (!event) {
    return (
      <Box p={3}>
        <Typography variant="body1">Loading event data...</Typography>
      </Box>
    );
  }

  // Exclude cancelled tickets from attendee metrics
  const activeTickets = (tickets || []).filter((t) => !t.cancellation);

  // Calculate metrics (total collected and refunded; display net revenue)
  const ticketsSold = activeTickets.length;
  const totalOrders = (orders || []).length;
  const totalCollected = stats?.totalUsdCents || 0;
  const refundedUsdCents = stats?.refundedUsdCents ?? 0;
  const totalRevenue = totalCollected - refundedUsdCents;
  const subtotalRevenue = stats?.subtotalUsdCents || 0;
  const promoDiscounts = stats?.promoCodeDiscountUsdCents || 0;
  const capacity = stats?.capacity;
  const capacityRemaining = capacity ? capacity - ticketsSold : null;

  // Calculate check-ins
  // A ticket is considered checked in if it has either:
  // - a global `checkIn` object with `createdAt`, or
  // - at least one truthy entry in `sessionCheckIns`
  const checkedInTickets = activeTickets.filter((ticket) => {
    const hasGlobalCheckIn =
      ticket.checkIn && ticket.checkIn.createdAt;

    const sessionCheckIns = ticket.sessionCheckIns || {};
    const hasSessionCheckIn = Object.values(sessionCheckIns).some(Boolean);

    return hasGlobalCheckIn || hasSessionCheckIn;
  }).length;
  const checkInRate =
    ticketsSold > 0 ? Math.round((checkedInTickets / ticketsSold) * 100) : 0;

  // Calculate average order value
  const averageOrderValue =
    totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;

  // Event status (same logic and values as event list / EventStatusView)
  const eventForStatus = {
    ticketTypes: ticketTypes || [],
    publishedAt,
    endsAt,
    stats: { ...(stats || {}), sold: ticketsSold, capacity: stats?.capacity },
  };
  const { status: eventStatus } = statusForEvent(eventForStatus);

  const eventStatusDisplay =
    squareLocationError
      ? "Payment Error"
      : {
          draft: "Draft",
          pending: "Sale pending",
          onSale: "On sale",
          soldOut: "Sold out",
          ended: "Ended",
        }[eventStatus] ?? "Unknown";

  const eventStatusSubtitle =
    squareLocationError
      ? "Square location configuration error"
      : {
          draft: "Event not yet published",
          pending: "Ticket sales have not started",
          onSale: "Tickets are available for purchase",
          soldOut: "All tickets have been sold",
          ended: "Event has concluded",
        }[eventStatus] ?? "";

  return (
    <Box
      marginTop="20px"
      marginBottom={{ xs: "90px", md: "0px" }}
      data-testid="event-dashboard"
    >
      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Revenue"
            value={priceText(totalRevenue)}
            subtitle={[
              refundedUsdCents > 0
                ? `Collected: ${priceText(totalCollected)}. Refunded: ${priceText(refundedUsdCents)}`
                : null,
              !(refundedUsdCents > 0) &&
              subtotalRevenue !== totalCollected &&
              promoDiscounts > 0
                ? `After ${priceText(promoDiscounts)} in discounts`
                : null,
            ]
              .filter(Boolean)
              .join(". ")}
            icon={AttachMoneyIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Tickets Sold"
            value={capacity ? `${ticketsSold} / ${capacity}` : ticketsSold}
            subtitle={
              capacity ? `${capacityRemaining} remaining` : "Unlimited capacity"
            }
            icon={ConfirmationNumberIcon}
            color="primary"
            progressValue={capacity ? ticketsSold : null}
            progressMax={capacity || null}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Orders"
            value={totalOrders}
            subtitle={`${priceText(averageOrderValue)} average order value`}
            icon={ShoppingCartIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Check-ins"
            value={checkedInTickets}
            subtitle={`${checkInRate}% check-in rate`}
            icon={CheckCircleIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Promo Discounts"
            value={priceText(promoDiscounts)}
            subtitle={
              promoDiscounts > 0
                ? `${Math.round(
                    (promoDiscounts / subtotalRevenue) * 100,
                  )}% of revenue`
                : "No discounts applied"
            }
            icon={TrendingUpIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Event Status"
            value={eventStatusDisplay}
            subtitle={eventStatusSubtitle}
            icon={PeopleIcon}
            color={
              squareLocationError
                ? "error"
                : eventStatus === "onSale"
                ? "success"
                : "default"
            }
          />
        </Grid>
      </Grid>

      {/* Ticket Types and Recent Orders */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <TicketTypeBreakdown ticketTypes={ticketTypes} eventId={eventId} />
        </Grid>
        <Grid item xs={12} md={6}>
          <RecentOrders orders={orders} eventId={eventId} />
        </Grid>
      </Grid>

      {/* Promo Codes and Affiliates */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <PromoCodeUsage
            promoCodes={promoCodes}
            tickets={activeTickets}
            eventId={eventId}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AffiliatesPerformance
            affiliates={affiliates}
            tickets={activeTickets}
            orders={orders}
            eventId={eventId}
          />
        </Grid>
      </Grid>

      {/* Audience map (OSM; ticket locations from zipcode geocode) */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <AudienceMapOSM
            event={event}
            tickets={activeTickets}
            ticketTypes={ticketTypes}
            title="Audience Locations"
          />
        </Grid>
      </Grid>

      {/* Sessions */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <SessionsOverview sessions={sessions} event={event} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EventDashboard;
